import { isPresetWidgetType } from '../presets/registry';
import { isChartWidgetType } from '../widgets/registry';
import { isDashboardDataSource } from './dashboardData';
import { widgetUsesReportQueries } from './reportQueries';

function widgetLabel(widget) {
  return widget?.title || widget?.type || widget?.id || 'ویجت';
}

function pushError(errors, { reportId, tabId, widgetId, field, message }) {
  errors.push({ reportId, tabId, widgetId, field, message });
}

function getSqlText(widget) {
  return String(widget?.sqlQuery || widget?.query?.sql || '').trim();
}

function validateChartWidget(widget, ctx, errors) {
  const chartConfig = widget.chartConfig || {};
  const xKey = String(chartConfig.xKey || '').trim();
  const series = Array.isArray(chartConfig.series) ? chartConfig.series : [];
  const seriesWithKey = series.filter((s) => s && String(s.key || '').trim());

  if (!xKey) {
    pushError(errors, {
      ...ctx,
      field: 'chartConfig.xKey',
      message: `ویجت «${widgetLabel(widget)}»: کلید محور / دسته (X) مشخص نشده است.`,
    });
  }

  if (seriesWithKey.length === 0) {
    pushError(errors, {
      ...ctx,
      field: 'chartConfig.series',
      message: `ویجت «${widgetLabel(widget)}»: حداقل یک سری نمودار با کلید معتبر لازم است.`,
    });
  }
}

function validateSqlWidget(widget, ctx, errors) {
  if (getSqlText(widget)) return;
  pushError(errors, {
    ...ctx,
    field: 'sqlQuery',
    message: `ویجت «${widgetLabel(widget)}»: متن SQL خالی است.`,
  });
}

function validateWidget(widget, ctx, errors) {
  if (!widget || typeof widget !== 'object') {
    pushError(errors, {
      ...ctx,
      field: 'widget',
      message: 'ویجت نامعتبر است.',
    });
    return;
  }

  if (!widget.id) {
    pushError(errors, {
      ...ctx,
      field: 'id',
      message: 'شناسه ویجت مشخص نیست.',
    });
  }

  const type = widget.type;
  if (!type || typeof type !== 'string') {
    pushError(errors, {
      ...ctx,
      field: 'type',
      message: `ویجت «${widgetLabel(widget)}»: نوع ویجت مشخص نیست.`,
    });
    return;
  }

  if (isPresetWidgetType(type)) {
    pushError(errors, {
      ...ctx,
      field: 'type',
      message: `ویجت «${widgetLabel(widget)}»: ویجت‌های پیش‌فرض حذف شده‌اند؛ با کارت KPI / جدول / نمودار جایگزین کنید.`,
    });
    return;
  }

  if (!isDashboardDataSource(widget) && !widgetUsesReportQueries(widget)) {
    validateSqlWidget(widget, ctx, errors);
  }

  if (isChartWidgetType(type)) {
    validateChartWidget(widget, ctx, errors);
  }
}

/**
 * Validate a reports config envelope before Designer save.
 * Pure / deterministic — does not mutate input.
 *
 * Only checks fields that are clearly required for widgets to function.
 * Does not reject optional styling, layout, labels, or empty filters.
 */
export function validateReportsConfig(config) {
  const errors = [];
  const reports = config?.reports;

  if (!Array.isArray(reports)) {
    return {
      valid: false,
      errors: [{
        reportId: null,
        tabId: null,
        widgetId: null,
        field: 'reports',
        message: 'ساختار تنظیمات گزارش نامعتبر است.',
      }],
    };
  }

  reports.forEach((report) => {
    if (!report || typeof report !== 'object') {
      pushError(errors, {
        reportId: null,
        tabId: null,
        widgetId: null,
        field: 'report',
        message: 'یکی از گزارش‌ها نامعتبر است.',
      });
      return;
    }

    const reportId = report.id || null;
    const tabs = Array.isArray(report.tabs) ? report.tabs : [];

    tabs.forEach((tab) => {
      const tabId = tab?.id || null;
      const widgets = Array.isArray(tab?.widgets) ? tab.widgets : [];
      widgets.forEach((widget) => {
        validateWidget(widget, {
          reportId,
          tabId,
          widgetId: widget?.id || null,
        }, errors);
      });
    });
  });

  return {
    valid: errors.length === 0,
    errors,
  };
}

export function formatValidationSummary(errors, maxItems = 3) {
  if (!errors?.length) return '';
  const lines = errors.slice(0, maxItems).map((e) => e.message);
  const remaining = errors.length - lines.length;
  if (remaining > 0) {
    lines.push(`و ${remaining} مورد دیگر…`);
  }
  return lines.join('\n');
}

/** Errors that apply to a specific widget (for the editor panel). */
export function errorsForWidget(errors, widgetId) {
  if (!widgetId || !errors?.length) return [];
  return errors.filter((e) => e.widgetId === widgetId);
}
